// Contract types for the AI trip-parse endpoint (aiApi.parseTrip).
// The backend agent implements this shape; the wizard codes to it.

export interface ParsedRoute {
  origin?: string;
  destination?: string;
  departureDate?: string;
  earliest_departure?: string;
  latest_arrival?: string;
  direct_flights_only?: boolean;
  leg_label?: string;
}

export interface CarrierFilter {
  mode: "include" | "exclude";
  airlines: string[];
}

export interface ParseTripResponse {
  name?: string;
  description?: string;
  routes?: ParsedRoute[];
  carrierFilter?: CarrierFilter | null;
  coachNotes?: string;
  /* Party size inferred from the description ("my wife and I" = 2). */
  passengers?: number;
  /* True when the home/origin airport was guessed (no origin stated). */
  origin_assumed?: boolean;
  tripType?: string;
  intelligence_applied?: string[];
  research?: {
    sourceUrl?: string;
    provider?: string;
    structured?: unknown;
    confidence?: number;
  };
}

export type ParseErrorCode = "AI_SERVICE_DOWN" | "PARSE_FAILED" | "NO_ROUTES" | "NEEDS_INFO";

export interface ParseErrorBody {
  error: {
    code: ParseErrorCode;
    message: string;
  };
}

export interface ParseErrorState {
  code: ParseErrorCode | "UNKNOWN";
  message: string;
}

// Accordion section identifiers used for the controlled open/close state.
export type AccordionSection = "routes" | "dates" | "preferences" | "passengers";

/**
 * Normalize an axios/fetch error into a distinct, user-facing parse error.
 * Falls back to UNKNOWN (treated like a soft failure: manual entry still works).
 */
export function normalizeParseError(err: unknown): ParseErrorState {
  const anyErr = err as {
    response?: { data?: Partial<ParseErrorBody> };
    message?: string;
  };
  const body = anyErr?.response?.data?.error;
  if (
    body?.code === "AI_SERVICE_DOWN" ||
    body?.code === "PARSE_FAILED" ||
    body?.code === "NO_ROUTES" ||
    body?.code === "NEEDS_INFO"
  ) {
    return { code: body.code, message: body.message || defaultMessageFor(body.code) };
  }
  return {
    code: "UNKNOWN",
    message:
      anyErr?.message ||
      "Something went wrong reading that. Open the sections below to enter your trip manually.",
  };
}

export function defaultMessageFor(code: ParseErrorCode): string {
  switch (code) {
    case "AI_SERVICE_DOWN":
      return "AI is temporarily unavailable — you can fill in the details below.";
    case "PARSE_FAILED":
      return "Couldn't read that — try rephrasing, or open the sections below to enter details.";
    case "NO_ROUTES":
      return "I couldn't find routes in that — add them below.";
    case "NEEDS_INFO":
      return "I need a bit more to plan this — tell me the destination and dates, or fill in the sections below.";
  }
}
